import { DEFAULT_LIVE_RANGE_HOURS, OVERVIEW_AVERAGE_KEY, SIGNAL_MAIN_FIELD, SIGNAL_OVERLAP_FIELDS } from './statisticsOverlayConstants';
import { DEFAULT_LIVE_WINDOW_MINUTES } from './machineStatDataConstants';
import { getAllStatisticsAreaIdentifiers, getDoubleAreaIdentifiers, getEfficiencyAreaIdentifiers, getErrorAreaIdentifiers } from './statisticsDatasetHelpers';

export const toLocalInput = (dateValue) => {
  const date = new Date(dateValue);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

export const hoursAgoInput = (hours = DEFAULT_LIVE_RANGE_HOURS) => toLocalInput(Date.now() - hours * 60 * 60 * 1000);
export const nowInput = () => toLocalInput(new Date());

export const formatAxisTick = (value, timeZone) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  if (typeof timeZone === 'string') return date.toLocaleTimeString([], { timeZone, hour: '2-digit', minute: '2-digit', hour12: false });
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const getProductionStopReason = (stop, locale = 'en') => stop.reason_translations?.[locale.split('-')[0]] || stop.reason_translations?.en || stop.reason;

export const formatTooltipDate = (value, timeZone) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], typeof timeZone === 'string' ? { timeZone } : undefined);
};

export const toDisplaySpeed = (value, unit) => (typeof value === 'number' && unit === 'm/h' ? value * 60 : value);

export const formatStatValue = (value, digits = 2, unit = '') => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  if (typeof value !== 'number') return value;
  return `${value.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
};

export const formatPercentValue = (value, digits = 1) => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  if (typeof value !== 'number') return value;
  return `${value.toFixed(digits)}%`;
};

export const formatTooltipMetricValue = (entry) => {
  if (!entry) return 'Unavailable';
  if (entry.unit === 'pcs' && typeof entry.value === 'number') return `${Math.round(entry.value).toLocaleString()} pcs`;
  if (entry.dataKey === 'efficiency' || entry.dataKey === OVERVIEW_AVERAGE_KEY || String(entry.dataKey || '').startsWith('efficiency_')) {
    return formatPercentValue(entry.value);
  }
  const signalField = [...SIGNAL_OVERLAP_FIELDS, SIGNAL_MAIN_FIELD].find((field) => entry.dataKey === field.key || entry.dataKey === `${field.key}Offset`);
  if (signalField) {
    const value = toFiniteNumber(entry.payload?.[signalField.key] ?? entry.value);
    return value === null ? 'Unavailable' : value > 0 ? 'Active' : 'Inactive';
  }
  return formatStatValue(entry.value);
};

export const formatDuration = (minutes) => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return 'Unavailable';

  const totalMinutes = Math.max(0, Math.round(minutes));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const remainingMinutes = totalMinutes % 60;

  return `${days}d ${hours}h ${remainingMinutes}m`;
};

export const formatErrorDuration = (minutes) => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return 'Unavailable';

  const totalSeconds = Math.max(0, Math.round(minutes * 60));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h ${remainingMinutes}m`;
  if (hours > 0) return `${hours}h ${remainingMinutes}m`;
  if (remainingMinutes > 0) return seconds > 0 ? `${remainingMinutes}m ${seconds}s` : `${remainingMinutes}m`;
  return `${seconds}s`;
};

const interpolateColor = (startColor, endColor, amount) => {
  const start = startColor.match(/\w\w/g).map((value) => parseInt(value, 16));
  const end = endColor.match(/\w\w/g).map((value) => parseInt(value, 16));
  const clampedAmount = Math.max(0, Math.min(amount, 1));

  return `#${start
    .map((startValue, index) =>
      Math.round(startValue + (end[index] - startValue) * clampedAmount)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
};

export const getErrorDurationColor = (minutes, totalMinutes) => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || typeof totalMinutes !== 'number' || !Number.isFinite(totalMinutes) || totalMinutes <= 0) return '#22c55e';

  const ratio = Math.max(0, Math.min(minutes / totalMinutes, 1));
  if (ratio <= 0.5) {
    return interpolateColor('#22c55e', '#f59e0b', ratio / 0.5);
  }

  return interpolateColor('#f59e0b', '#dc2626', (ratio - 0.5) / 0.5);
};

export const getPercentageSeverityColor = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '#22c55e';

  const ratio = Math.max(0, Math.min(value / 100, 1));
  if (ratio <= 0.5) {
    return interpolateColor('#dc2626', '#f59e0b', ratio / 0.5);
  }

  return interpolateColor('#f59e0b', '#22c55e', (ratio - 0.5) / 0.5);
};

export const calculateRangeDurationMinutes = (rangeMode, from, to) => {
  if (rangeMode === 'live') return DEFAULT_LIVE_WINDOW_MINUTES;
  if (!from || !to) return null;

  const fromTime = Date.parse(from);
  const toTime = Date.parse(to);
  if (Number.isNaN(fromTime) || Number.isNaN(toTime)) return null;

  return Math.abs(toTime - fromTime) / 60000;
};

const toFiniteNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const parseTimelineDate = (value) => {
  if (!value) return NaN;
  const date = String(value);
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(date) ? date : `${date}Z`);
};

export const addCumulativeOverlayGraphData = ({ chartData, graphData, graph, series }) => {
  if (!chartData?.length || !graph?.key || !series?.length) return chartData;

  return series.reduce((result, currentSeries) => {
    const cumulativePoints = (graphData?.[currentSeries.identifier] ?? [])
      .map((point) => ({ time: parseTimelineDate(point?.date), value: toFiniteNumber(point?.[`${graph.key}Cumulative`]) }))
      .filter((point) => !Number.isNaN(point.time) && point.value !== null)
      .sort((left, right) => left.time - right.time);
    if (!cumulativePoints.length) return result;

    let pointIndex = 0;
    let currentValue = null;

    return result.map((point) => {
      const time = parseTimelineDate(point?.date);
      while (pointIndex < cumulativePoints.length && cumulativePoints[pointIndex].time <= time) {
        currentValue = cumulativePoints[pointIndex].value;
        pointIndex += 1;
      }

      return currentValue === null ? point : { ...point, [currentSeries.dataKey]: currentValue };
    });
  }, chartData);
};

const averageNumbers = (values) => {
  const validValues = values.filter((value) => typeof value === 'number' && Number.isFinite(value));
  if (!validValues.length) return null;
  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
};

const calculateEfficiency = (actualSpeed, expectedSpeed) => {
  if (expectedSpeed <= 0) return null;
  return (actualSpeed / expectedSpeed) * 100;
};

const normalizeBucketSpeed = (value, durationMinutes) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;
  return typeof durationMinutes === 'number' && Number.isFinite(durationMinutes) && durationMinutes > 1 ? value / durationMinutes : value;
};

const calculateEfficiencyForPoints = (points, denominatorKey, disregardedRatioByDate) => {
  let actualSpeedTotal = 0;
  let denominatorTotal = 0;

  points.forEach((point) => {
    const actualSpeed = toFiniteNumber(point.actualSpeed);
    const denominator = toFiniteNumber(point[denominatorKey]);
    const disregardedRatio = Math.max(0, Math.min(disregardedRatioByDate?.get(point.date) ?? 0, 1));
    const adjustedDenominator = denominator === null ? null : denominator * (1 - disregardedRatio);
    if (actualSpeed === null || adjustedDenominator === null || adjustedDenominator <= 0) return;

    actualSpeedTotal += actualSpeed;
    denominatorTotal += adjustedDenominator;
  });

  return { actualTotal: actualSpeedTotal, expectedTotal: denominatorTotal, efficiency: calculateEfficiency(actualSpeedTotal, denominatorTotal) };
};

const aggregateNamedMetrics = (entriesByDataset, numericKeys, averageValues = false) => {
  const totals = new Map();
  const keys = Array.isArray(numericKeys) ? numericKeys : [numericKeys];

  entriesByDataset.forEach((entries) => {
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      const name = entry?.name;
      if (!name) return;

      const total = totals.get(name) ?? Object.fromEntries(keys.map((numericKey) => [numericKey, { sum: 0, count: 0 }]));
      keys.forEach((numericKey) => {
        const value = toFiniteNumber(entry?.[numericKey]);
        if (value === null) return;

        total[numericKey].sum += value;
        total[numericKey].count += 1;
      });
      totals.set(name, total);
    });
  });

  return [...totals.entries()]
    .map(([name, total]) => ({
      name,
      ...Object.fromEntries(keys.map((numericKey) => [numericKey, total[numericKey].count ? (averageValues ? total[numericKey].sum / total[numericKey].count : total[numericKey].sum) : null]))
    }))
    .sort((left, right) => (right[keys[0]] ?? 0) - (left[keys[0]] ?? 0));
};

export const buildPointMetricTotals = (points, key, numericKeys) =>
  aggregateNamedMetrics(
    (Array.isArray(points) ? points : []).map((point) => point?.[key]),
    numericKeys
  );

export const buildPointMetricAverages = (points, key, numericKeys) =>
  aggregateNamedMetrics(
    (Array.isArray(points) ? points : []).map((point) => point?.[key]),
    numericKeys,
    true
  );

const sortPointsByDate = (points) =>
  [...points].sort((left, right) => {
    const leftTime = Date.parse(left?.date);
    const rightTime = Date.parse(right?.date);
    return (Number.isNaN(leftTime) ? 0 : leftTime) - (Number.isNaN(rightTime) ? 0 : rightTime);
  });

const getAggregatedBoolSignalMinutes = (point) => {
  const value = point?.aggregatedBoolSignals;
  if (typeof value !== 'number' || !Number.isFinite(value)) return value === true ? 1 : 0;

  const minutes = Math.max(0, value);
  const durationMinutes = toFiniteNumber(point?.durationMinutes);
  return durationMinutes && durationMinutes > 0 ? Math.min(minutes, durationMinutes) : minutes;
};

const calculateAggregatedBoolSignalMinutes = (points) => {
  const sortedPoints = sortPointsByDate(Array.isArray(points) ? points : []);
  return sortedPoints.reduce((sum, point) => sum + getAggregatedBoolSignalMinutes(point), 0);
};

const buildDatasetEfficiencyPoints = (dataByArea, dataset) => {
  const pointsByDate = new Map();

  getEfficiencyAreaIdentifiers(dataset).forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      if (!pointsByDate.has(point.date)) {
        pointsByDate.set(point.date, {
          date: point.date,
          durationMinutes: point.durationMinutes,
          actualSpeed: 0,
          expectedSpeed: 0,
          contractSpeed: 0,
          hasContractSpeed: false
        });
      }

      const combinedPoint = pointsByDate.get(point.date);
      combinedPoint.runningMetres = point.runningMetres ?? combinedPoint.runningMetres;
      combinedPoint.xcutDimensions = point.xcutDimensions ?? combinedPoint.xcutDimensions;
      combinedPoint.averageXcutLength = point.averageXcutLength ?? combinedPoint.averageXcutLength;
      combinedPoint.averageXcutWidth = point.averageXcutWidth ?? combinedPoint.averageXcutWidth;
      combinedPoint.averageXcutThickness = point.averageXcutThickness ?? combinedPoint.averageXcutThickness;
      combinedPoint.xcutDataCarriedForward = point.xcutDataCarriedForward ?? combinedPoint.xcutDataCarriedForward;
      const durationMinutes = toFiniteNumber(point.durationMinutes);
      if (durationMinutes !== null) {
        combinedPoint.durationMinutes = Math.max(combinedPoint.durationMinutes ?? 0, durationMinutes);
      }
      combinedPoint.actualSpeed += toFiniteNumber(point.actualSpeed) ?? 0;
      combinedPoint.expectedSpeed += toFiniteNumber(point.expectedSpeed) ?? 0;
      const contractSpeed = toFiniteNumber(point.contractSpeed);
      if (contractSpeed !== null) {
        combinedPoint.contractSpeed += contractSpeed;
        combinedPoint.hasContractSpeed = true;
      }
    });
  });

  return [...pointsByDate.values()].sort((left, right) => new Date(left.date) - new Date(right.date));
};

export const buildDatasetEfficiencyData = (dataByArea, dataset) =>
  buildDatasetEfficiencyPoints(dataByArea, dataset).map((point) => {
    const contractSpeed = point.hasContractSpeed ? point.contractSpeed : null;
    const actualSpeed = normalizeBucketSpeed(point.actualSpeed, point.durationMinutes);
    const expectedSpeed = normalizeBucketSpeed(point.expectedSpeed, point.durationMinutes);
    const displayContractSpeed = normalizeBucketSpeed(contractSpeed, point.durationMinutes);

    return {
      ...point,
      actualSpeed,
      expectedSpeed,
      contractSpeed: displayContractSpeed,
      aggregateSpeed: actualSpeed,
      efficiency: calculateEfficiency(actualSpeed, expectedSpeed),
      contractEfficiency: calculateEfficiency(actualSpeed, displayContractSpeed)
    };
  });

const buildAreaMetricTotals = (dataByArea, areaIdentifiers, key, numericKey) => {
  const shouldPrefixArea = areaIdentifiers.length > 1;

  return areaIdentifiers.flatMap((areaIdentifier) =>
    buildPointMetricTotals(dataByArea?.[areaIdentifier] ?? [], key, numericKey).map((entry) => ({
      ...entry,
      areaIdentifier,
      metricName: entry.name,
      name: shouldPrefixArea ? `${areaIdentifier} · ${entry.name}` : entry.name
    }))
  );
};

const buildAreaMetricAverages = (dataByArea, areaIdentifiers, key, numericKey) => {
  const shouldPrefixArea = areaIdentifiers.length > 1;

  return areaIdentifiers.flatMap((areaIdentifier) =>
    buildPointMetricAverages(dataByArea?.[areaIdentifier] ?? [], key, numericKey).map((entry) => ({
      ...entry,
      areaIdentifier,
      metricName: entry.name,
      name: shouldPrefixArea ? `${areaIdentifier} · ${entry.name}` : entry.name
    }))
  );
};

export const buildDatasetDoubleAverages = (dataByArea, dataset) => buildAreaMetricAverages(dataByArea, getDoubleAreaIdentifiers(dataset), 'doubles', 'value');

export const buildDatasetErrorTotals = (dataByArea, dataset) => buildAreaMetricTotals(dataByArea, getErrorAreaIdentifiers(dataset), 'errors', ['minutes', 'noFlowMinutes']);

export const calculateDatasetErrorDurationMinutes = (dataByArea, dataset) =>
  getErrorAreaIdentifiers(dataset).reduce((sum, areaIdentifier) => sum + calculateAggregatedBoolSignalMinutes(dataByArea?.[areaIdentifier] ?? []), 0);

export const calculateOverviewErrorDurationMinutes = (dataByArea, datasets) => {
  const minutesByDate = new Map();
  const areaIdentifiers = [...new Set((datasets ?? []).flatMap((dataset) => getErrorAreaIdentifiers(dataset)))];

  areaIdentifiers.forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      const minutes = getAggregatedBoolSignalMinutes(point);
      minutesByDate.set(point.date, Math.max(minutesByDate.get(point.date) ?? 0, minutes));
    });
  });

  return [...minutesByDate.values()].reduce((sum, minutes) => sum + minutes, 0);
};

const getNoFlowMinutes = (point) => {
  const value = point?.noFlowMinutes;
  if (typeof value !== 'number' || !Number.isFinite(value)) return value === true ? 1 : 0;

  const minutes = Math.max(0, value);
  const durationMinutes = toFiniteNumber(point?.durationMinutes);
  return durationMinutes && durationMinutes > 0 ? Math.min(minutes, durationMinutes) : minutes;
};

export const calculateDatasetNoFlowDurationMinutes = (dataByArea, dataset) =>
  getErrorAreaIdentifiers(dataset).reduce((sum, areaIdentifier) => sum + sortPointsByDate(dataByArea?.[areaIdentifier] ?? []).reduce((areaSum, point) => areaSum + getNoFlowMinutes(point), 0), 0);

export const calculateOverviewNoFlowDurationMinutes = (dataByArea, datasets) => {
  const minutesByDate = new Map();
  const areaIdentifiers = [...new Set((datasets ?? []).flatMap((dataset) => getErrorAreaIdentifiers(dataset)))];

  areaIdentifiers.forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      const minutes = getNoFlowMinutes(point);
      minutesByDate.set(point.date, Math.max(minutesByDate.get(point.date) ?? 0, minutes));
    });
  });

  return [...minutesByDate.values()].reduce((sum, minutes) => sum + minutes, 0);
};

export const getErrorSelectionKey = (areaIdentifier, errorName) => `${areaIdentifier ?? ''}:${errorName ?? ''}`;

const sumNamedErrorMinutes = (point, areaIdentifier, shouldInclude = () => true) =>
  (Array.isArray(point?.errors) ? point.errors : []).reduce((sum, error) => {
    if (!shouldInclude(getErrorSelectionKey(areaIdentifier, error?.name))) return sum;
    return sum + (toFiniteNumber(error?.minutes) ?? 0);
  }, 0);

const getFilteredErrorMinutes = (point, areaIdentifier, selectedErrorKeys, excludedErrorKeys) => {
  const durationMinutes = toFiniteNumber(point.durationMinutes);
  const namedErrorMinutes = sumNamedErrorMinutes(point, areaIdentifier);
  const aggregateErrorMinutes = getAggregatedBoolSignalMinutes(point);
  const totalErrorMinutes = Math.min(durationMinutes > 0 ? durationMinutes : Infinity, Math.max(0, aggregateErrorMinutes > 0 ? aggregateErrorMinutes : namedErrorMinutes));

  if (selectedErrorKeys.size) {
    return Math.min(
      totalErrorMinutes,
      sumNamedErrorMinutes(point, areaIdentifier, (errorKey) => selectedErrorKeys.has(errorKey))
    );
  }
  if (excludedErrorKeys.size) {
    return Math.max(
      0,
      totalErrorMinutes -
        Math.min(
          totalErrorMinutes,
          sumNamedErrorMinutes(point, areaIdentifier, (errorKey) => excludedErrorKeys.has(errorKey))
        )
    );
  }
  return totalErrorMinutes;
};

const buildDisregardedErrorRatioMap = (dataByArea, dataset, selectedErrorKeys, excludedErrorKeys) => {
  const ratioByDate = new Map();
  if (!selectedErrorKeys.size && !excludedErrorKeys.size) return ratioByDate;

  getErrorAreaIdentifiers(dataset).forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      const totalErrorMinutes = getFilteredErrorMinutes(point, areaIdentifier, new Set(), new Set());
      const includedErrorMinutes = getFilteredErrorMinutes(point, areaIdentifier, selectedErrorKeys, excludedErrorKeys);
      const disregardedMinutes = totalErrorMinutes - includedErrorMinutes;
      if (disregardedMinutes <= 0) return;

      const durationMinutes = toFiniteNumber(point.durationMinutes);
      const ratio = durationMinutes > 0 ? Math.min(1, disregardedMinutes / durationMinutes) : 1;
      ratioByDate.set(point.date, Math.max(ratioByDate.get(point.date) ?? 0, ratio));
    });
  });

  return ratioByDate;
};

export const calculateFilteredErrorDurationMinutes = (dataByArea, areaIdentifiers, selectedErrorKeys = new Set(), excludedErrorKeys = new Set()) => {
  const minutesByDate = new Map();

  (areaIdentifiers ?? []).forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;
      const minutes = getFilteredErrorMinutes(point, areaIdentifier, selectedErrorKeys, excludedErrorKeys);
      minutesByDate.set(point.date, Math.max(minutesByDate.get(point.date) ?? 0, minutes));
    });
  });

  return [...minutesByDate.values()].reduce((sum, minutes) => sum + minutes, 0);
};

export const buildDatasetEfficiencySummary = (dataByArea, dataset, selectedErrorKeys = new Set(), excludedErrorKeys = new Set()) => {
  const points = buildDatasetEfficiencyPoints(dataByArea, dataset);
  const disregardedRatioByDate = buildDisregardedErrorRatioMap(dataByArea, dataset, selectedErrorKeys, excludedErrorKeys);

  const efficiencyCalculation = calculateEfficiencyForPoints(points, 'expectedSpeed', disregardedRatioByDate);
  const contractEfficiencyCalculation = calculateEfficiencyForPoints(points, 'contractSpeed', disregardedRatioByDate);
  return {
    fullRangeEfficiency: efficiencyCalculation.efficiency,
    fullRangeContractEfficiency: contractEfficiencyCalculation.efficiency,
    efficiencyCalculation,
    contractEfficiencyCalculation
  };
};

const buildRangesFromRatioMap = (chartData, ratioByDate) => {
  const points = Array.isArray(chartData) ? chartData : [];
  return points.reduce((ranges, point, index, allPoints) => {
    const ratio = ratioByDate.get(point?.date);
    if (!ratio) return ranges;

    const nextDate = allPoints[index + 1]?.date;
    const previousDate = allPoints[index - 1]?.date;
    if (!nextDate && ratioByDate.has(previousDate)) return ranges;

    const x1 = nextDate ? point.date : previousDate;
    const x2 = nextDate ?? point.date;
    if (!x1 || !x2 || x1 === x2) return ranges;

    ranges.push({ x1, x2, ratio, fillOpacity: 0.04 + ratio * 0.28 });
    return ranges;
  }, []);
};

const buildErrorRatioMap = (dataByArea, areaIdentifiers, selectedErrorKeys, excludedErrorKeys) => {
  const ratioByDate = new Map();
  (areaIdentifiers ?? []).forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;
      const errors = Array.isArray(point?.errors) ? point.errors : [];
      const errorMins =
        selectedErrorKeys.size || excludedErrorKeys.size
          ? errors
              .filter((e) => {
                const errorKey = getErrorSelectionKey(areaIdentifier, e?.name);
                return selectedErrorKeys.size ? selectedErrorKeys.has(errorKey) : !excludedErrorKeys.has(errorKey);
              })
              .reduce((sum, e) => sum + (toFiniteNumber(e?.minutes) ?? 0), 0)
          : getAggregatedBoolSignalMinutes(point);
      if (errorMins <= 0) return;
      const duration = toFiniteNumber(point?.durationMinutes);
      const ratio = duration > 0 ? Math.min(1, errorMins / duration) : 1;
      ratioByDate.set(point.date, Math.max(ratioByDate.get(point.date) ?? 0, ratio));
    });
  });
  return ratioByDate;
};

const buildNoFlowRatioMap = (dataByArea, areaIdentifiers, selectedErrorKeys, excludedErrorKeys) => {
  const ratioByDate = new Map();
  (areaIdentifiers ?? []).forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;
      const errors = Array.isArray(point?.errors) ? point.errors : [];
      const noFlowMins =
        selectedErrorKeys.size || excludedErrorKeys.size
          ? errors
              .filter((e) => {
                const errorKey = getErrorSelectionKey(areaIdentifier, e?.name);
                return selectedErrorKeys.size ? selectedErrorKeys.has(errorKey) : !excludedErrorKeys.has(errorKey);
              })
              .reduce((sum, e) => sum + (toFiniteNumber(e?.noFlowMinutes) ?? 0), 0)
          : getNoFlowMinutes(point);
      if (noFlowMins <= 0) return;
      const duration = toFiniteNumber(point?.durationMinutes);
      const ratio = duration > 0 ? Math.min(1, noFlowMins / duration) : 1;
      ratioByDate.set(point.date, Math.max(ratioByDate.get(point.date) ?? 0, ratio));
    });
  });
  return ratioByDate;
};

export const buildErrorHighlightRanges = (dataByArea, areaIdentifiers, chartData, selectedErrorKeys = new Set(), excludedErrorKeys = new Set()) =>
  buildRangesFromRatioMap(chartData, buildErrorRatioMap(dataByArea, areaIdentifiers, selectedErrorKeys, excludedErrorKeys));

export const buildNoFlowHighlightRanges = (dataByArea, areaIdentifiers, chartData, selectedErrorKeys = new Set(), excludedErrorKeys = new Set()) =>
  buildRangesFromRatioMap(chartData, buildNoFlowRatioMap(dataByArea, areaIdentifiers, selectedErrorKeys, excludedErrorKeys));

export const buildOverviewEfficiencyDatasets = (datasets) => {
  const groupsByAreaKey = new Map();

  (datasets ?? []).forEach((dataset) => {
    const areaIdentifiers = getEfficiencyAreaIdentifiers(dataset);
    const errorAreaIdentifiers = getErrorAreaIdentifiers(dataset);
    const areaKey = areaIdentifiers.join('|');

    if (!groupsByAreaKey.has(areaKey)) {
      groupsByAreaKey.set(areaKey, {
        ...dataset,
        key: areaIdentifiers.join('_'),
        label: areaIdentifiers.join('/'),
        efficiencyAreaIdentifiers: areaIdentifiers,
        errorAreaIdentifiers,
        sourceDatasetKeys: [dataset.key]
      });
      return;
    }
    const group = groupsByAreaKey.get(areaKey);
    group.sourceDatasetKeys.push(dataset.key);
    group.errorAreaIdentifiers = [...new Set([...group.errorAreaIdentifiers, ...errorAreaIdentifiers])];
  });

  return [...groupsByAreaKey.values()];
};

export const buildOverviewData = (dataByArea, datasets) => {
  const pointsByDate = new Map();
  const overviewDatasets = buildOverviewEfficiencyDatasets(datasets);

  overviewDatasets.forEach((dataset) => {
    const areaPoints = buildDatasetEfficiencyData(dataByArea, dataset);

    areaPoints.forEach((point) => {
      if (!point?.date) return;
      const efficiency = toFiniteNumber(point.efficiency);
      if (efficiency === null) return;

      if (!pointsByDate.has(point.date)) {
        pointsByDate.set(point.date, {
          date: point.date
        });
      }

      pointsByDate.get(point.date)[`efficiency_${dataset.key}`] = efficiency;
    });
  });

  return [...pointsByDate.values()]
    .sort((left, right) => new Date(left.date) - new Date(right.date))
    .map((point) => {
      const efficiencyValues = overviewDatasets.map((dataset) => toFiniteNumber(point[`efficiency_${dataset.key}`])).filter((value) => value !== null);

      return {
        ...point,
        [OVERVIEW_AVERAGE_KEY]: averageNumbers(efficiencyValues)
      };
    });
};

export const buildSignalOverlapData = (dataByArea, datasets, selectedAreaIdentifier = '') => {
  const pointsByDate = new Map();
  const areaIdentifiers = selectedAreaIdentifier ? [selectedAreaIdentifier] : getAllStatisticsAreaIdentifiers(datasets);

  areaIdentifiers.forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      if (!pointsByDate.has(point.date)) {
        pointsByDate.set(point.date, { date: point.date });
      }

      const combinedPoint = pointsByDate.get(point.date);
      [...SIGNAL_OVERLAP_FIELDS, SIGNAL_MAIN_FIELD].forEach((field) => {
        const value = toFiniteNumber(point[field.key]);
        if (value === null) return;

        combinedPoint[field.key] = Math.max(toFiniteNumber(combinedPoint[field.key]) ?? 0, value > 0 ? 1 : 0);
      });
    });
  });

  return [...pointsByDate.values()]
    .sort((left, right) => new Date(left.date) - new Date(right.date))
    .map((point) => ({
      ...point,
      ...Object.fromEntries(
        SIGNAL_OVERLAP_FIELDS.map((field, index) => {
          const value = toFiniteNumber(point[field.key]) ?? 0;
          const offset = index * 0.03;
          return [`${field.key}Offset`, value > 0 ? 1 + offset : offset];
        })
      )
    }));
};

export const buildOverviewSummary = (dataByArea, datasets, selectedErrorKeys = new Set(), excludedErrorKeys = new Set()) => {
  const summaries = buildOverviewEfficiencyDatasets(datasets).map((dataset) => {
    const points = buildDatasetEfficiencyData(dataByArea, dataset);
    const disregardedRatioByDate = buildDisregardedErrorRatioMap(dataByArea, dataset, selectedErrorKeys, excludedErrorKeys);

    return {
      efficiency: calculateEfficiencyForPoints(points, 'expectedSpeed', disregardedRatioByDate).efficiency,
      contractEfficiency: calculateEfficiencyForPoints(points, 'contractSpeed', disregardedRatioByDate).efficiency
    };
  });

  return {
    efficiencyCalculations: summaries,
    fullRangeEfficiency: averageNumbers(summaries.map((summary) => summary.efficiency)),
    fullRangeContractEfficiency: averageNumbers(summaries.map((summary) => summary.contractEfficiency)),
    actualSpeedAverage: null,
    expectedSpeedAverage: null,
    contractSpeedAverage: null,
    latestActualSpeed: null,
    latestExpectedSpeed: null,
    latestContractSpeed: null
  };
};

export const buildOverviewDetails = (dataByArea, datasets) => {
  return {
    errors: (datasets ?? []).flatMap((dataset) => buildDatasetErrorTotals(dataByArea, dataset).map((entry) => ({ ...entry, name: `${dataset.label} · ${entry.name}` }))),
    doubles: (datasets ?? []).flatMap((dataset) => buildDatasetDoubleAverages(dataByArea, dataset).map((entry) => ({ ...entry, name: `${dataset.label} · ${entry.name}` })))
  };
};

export const getSortedSelectionRange = (selectionStart, selectionEnd) =>
  selectionStart && selectionEnd ? [selectionStart, selectionEnd].sort((left, right) => Date.parse(left) - Date.parse(right)) : null;
